import { useMemo } from 'react'
import { Picker } from '../Picker'
import type { OrgUnitOption } from '../../lib/admin'
import { isCountryChoice, scopeIds, scopeOptions, type BuPlace } from '../../lib/where'

/**
 * 159. Where a scoped role is granted for, chosen from the Where tree.
 *
 * David, 11 Oct: Dustin is in GEOTECH, but his HR Administrator role is
 * Canada-wide, and GEOTECH people are working in BU 51 in the US — "I'd give
 * him Canada and BU51". A flat list of org units made that six separate
 * grants; here it is two choices.
 *
 * ── WHAT A CHOICE GRANTS IS SHOWN BEFORE IT IS GRANTED ──────────────────────
 *
 * There is no country org unit (lib/where.ts), so "Canada" becomes the legal
 * entities in Canada TODAY — one grant each. The line under the picker names
 * them, and says that an entity added later is not covered: the alternative is
 * an administrator believing they granted a country.
 *
 * `units` is what the caller may name for this role (scopeOptionsFor): the
 * role's scope types and a regional holder's region. The server checks both
 * again (assign_role); this only keeps refusals off the list.
 */
export function ScopePicker({
  roleName,
  units,
  places,
  selected,
  onChange,
}: {
  roleName: string
  units: OrgUnitOption[]
  places: BuPlace[]
  selected: string[]
  onChange: (next: string[]) => void
}) {
  const { options, tree } = useMemo(() => scopeOptions(units, places), [units, places])
  const ids = scopeIds(selected, tree)
  const names = ids
    .map((id) => units.find((u) => u.orgUnitId === id)?.name ?? null)
    .filter((n): n is string => n !== null)
  const country = selected.some(isCountryChoice)

  return (
    <div className="scopepick">
      <Picker
        label={`Where ${roleName} applies`}
        anyLabel="Choose…"
        searchPlaceholder="Country, entity or business unit"
        options={options}
        selected={selected}
        onChange={onChange}
      />
      {ids.length > 0 && (
        <p className="scopepick__grants admin__muted">
          {ids.length === 1 ? 'One grant' : `${ids.length} grants, one each`}: {names.join(', ')}.
          {country && ' A country means the legal entities in it today; one added later is not included.'}
        </p>
      )}
    </div>
  )
}
